'use client';

/**
 * Post org-creation setup wizard: Industry → Region → Summary.
 *
 * Phase B (wired): every completed step saves immediately AND stamps its
 * section in organization.setup_wizard_progress (server-side merge via the
 * wizardSection body field):
 * - Industry → PUT /api/platform/label-aliases (industry label preset +
 *   display_player_sales) + wizardIndustryId so the Brand Values wizard can
 *   flavor wording/presets.
 * - Region → PUT /api/platform/settings (base_currency,
 *   default_number_format, default_date_format, default_timezone).
 *
 * Already-completed sections are not re-asked: the wizard shows only the
 * missing steps (askIndustry/askRegion) + the summary. Closing/skipping is
 * session-only dismissal handled by the parent (SetupWizardController).
 *
 * Also reusable from Settings → Valuations via fromSettings (industry step
 * only, settings-appropriate wording) — replaces the old "Quick Fill by
 * Industry" select.
 */

import { useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useDarkMode } from '@/lib/platform/contexts/dark-mode-context';
import { PrimaryButton } from '@/components/platform/buttons';
import Select from '@/components/platform/forms/Select';
import { WIZARD_INDUSTRIES, type WizardIndustryId } from '@/lib/platform/data/brand-value-presets';
import { getCountryDefaults, type CountryDefaults } from '@/lib/platform/data/country-defaults';
import { getCountryOptions, getCountryName } from '@/lib/platform/data/countries';
import { CURRENCIES } from '@/lib/platform/utils/currency';
import { getTimezoneOptions } from '@/lib/platform/utils/timezones';
import { LABEL_EXAMPLES, type LabelKey } from '@/lib/platform/hooks/use-alias';
import { queryKeys } from '@/lib/platform/hooks/queries/query-keys';
import { fetchWithBypass } from '@/lib/platform/utils/fetch';
import { logger } from '@/lib/platform/utils/logger';
import WizardShell from './WizardShell';

const NUMBER_FORMAT_OPTIONS = [
  { value: 'eu', label: '1.234,56' },
  { value: 'us', label: '1,234.56' },
  { value: 'fr', label: '1 234,56' },
];

const DATE_FORMAT_OPTIONS = [
  { value: 'DD-MM-YYYY', label: 'DD-MM-YYYY' },
  { value: 'MM/DD/YYYY', label: 'MM/DD/YYYY' },
  { value: 'YYYY-MM-DD', label: 'YYYY-MM-DD' },
];

/** Alias keys the industry preset fills on the settings labels form */
const INDUSTRY_ALIAS_KEYS: readonly LabelKey[] = [
  'sales_value_title',
  'sales_value_description',
  'merchandise_revenue',
  'matchday_revenue',
  'sponsorship_module',
  'sponsor_singular',
  'sponsor_plural',
  'package_singular',
  'package_plural',
];

/** Industry label preset from LABEL_EXAMPLES (single source for all labels) */
function buildIndustryAliases(industry: WizardIndustryId): Record<string, string> {
  const aliases: Record<string, string> = {};
  for (const key of INDUSTRY_ALIAS_KEYS) {
    aliases[key] = LABEL_EXAMPLES[key][industry];
  }
  // Only Sports has player sales
  aliases.display_player_sales = String(industry === 'sports');
  return aliases;
}

type Step = 'industry' | 'region' | 'summary';

interface SetupWizardModalProps {
  isOpen: boolean;
  /** Session-only dismissal ("Skip for now" / close button) */
  onClose: () => void;
  organizationId: string;
  /** Org country from signup (organization.country_code), if set */
  initialCountryCode?: string | null;
  /** Ask the Industry step (section not yet completed) */
  askIndustry: boolean;
  /** Ask the Region step (section not yet completed) */
  askRegion: boolean;
  /**
   * Re-run from Settings (replaces the old "Quick Fill by Industry" select):
   * swaps the onboarding intro copy for settings-appropriate wording.
   */
  fromSettings?: boolean;
}

export default function SetupWizardModal({
  isOpen,
  onClose,
  organizationId,
  initialCountryCode,
  askIndustry,
  askRegion,
  fromSettings = false,
}: SetupWizardModalProps) {
  const { darkMode } = useDarkMode();
  const queryClient = useQueryClient();

  const signupCountry = initialCountryCode?.toUpperCase() ?? null;

  // Only the missing sections are asked; the summary always closes the flow
  const steps: readonly Step[] = [
    ...(askIndustry ? (['industry'] as const) : []),
    ...(askRegion ? (['region'] as const) : []),
    'summary' as const,
  ];

  const [step, setStep] = useState<Step>(steps[0]);
  const [industry, setIndustry] = useState<WizardIndustryId | null>(null);
  const [countryCode, setCountryCode] = useState<string>(signupCountry ?? 'DK');
  const [region, setRegion] = useState<CountryDefaults>(() =>
    getCountryDefaults(signupCountry ?? 'DK')
  );
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  // Reset to a fresh flow each time the modal opens (adjust-state-during-render
  // — the component stays mounted while isOpen toggles, e.g. on Settings)
  const [prevIsOpen, setPrevIsOpen] = useState(isOpen);
  if (prevIsOpen !== isOpen) {
    setPrevIsOpen(isOpen);
    if (isOpen) {
      setStep(steps[0]);
      setIndustry(null);
      setCountryCode(signupCountry ?? 'DK');
      setRegion(getCountryDefaults(signupCountry ?? 'DK'));
      setSaveError(null);
    }
  }

  const stepIndex = steps.indexOf(step);
  const countryName = getCountryName(countryCode, 'en');

  const applyCountry = (code: string) => {
    setCountryCode(code);
    setRegion(getCountryDefaults(code));
  };

  const goTo = (next: Step) => {
    setSaveError(null);
    setStep(next);
  };

  const invalidateOrganization = async () => {
    await queryClient.invalidateQueries({ queryKey: queryKeys.organization.all });
  };

  /** Industry step Continue: save label preset + stamp the section */
  const saveIndustry = async () => {
    if (!industry) return;
    setSaving(true);
    setSaveError(null);
    try {
      const response = await fetchWithBypass('/api/platform/label-aliases', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          organizationId,
          labelAliases: buildIndustryAliases(industry),
          wizardSection: 'industry',
          wizardIndustryId: industry,
        }),
      });
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to save industry');
      }
      await invalidateOrganization();
      goTo(steps[stepIndex + 1]);
    } catch (err) {
      logger.error('[SetupWizard] Failed to save industry:', err);
      setSaveError(err instanceof Error ? err.message : 'Failed to save industry');
    } finally {
      setSaving(false);
    }
  };

  /** Region step Continue: save regional settings + stamp the section */
  const saveRegion = async () => {
    setSaving(true);
    setSaveError(null);
    try {
      const response = await fetchWithBypass('/api/platform/settings', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          organizationId,
          updates: {
            base_currency: region.currency,
            default_number_format: region.numberFormat,
            default_date_format: region.dateFormat,
            default_timezone: region.timezone,
          },
          wizardSection: 'region',
        }),
      });
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to save region settings');
      }
      await invalidateOrganization();
      goTo(steps[stepIndex + 1]);
    } catch (err) {
      logger.error('[SetupWizard] Failed to save region settings:', err);
      setSaveError(err instanceof Error ? err.message : 'Failed to save region settings');
    } finally {
      setSaving(false);
    }
  };

  const headingClass = `text-lg font-semibold ${darkMode ? 'text-gray-100' : 'text-gray-900'}`;
  const bodyClass = `mt-1 text-sm ${darkMode ? 'text-gray-400' : 'text-gray-500'}`;
  const linkBtnClass = `text-xs ${
    darkMode ? 'text-gray-500 hover:text-gray-300' : 'text-gray-400 hover:text-gray-600'
  }`;
  const errorClass = 'mt-3 text-xs text-red-500';

  return (
    <WizardShell
      isOpen={isOpen}
      onClose={onClose}
      stepLabel={`Step ${stepIndex + 1} of ${steps.length}`}
      segmentsTotal={steps.length}
      segmentsFilled={stepIndex + 1}
    >
      {step === 'industry' && (
        <div>
          <h2 className={headingClass}>
            {fromSettings ? 'Choose your industry' : 'Welcome to Valiyou'}
          </h2>
          <p className={`${bodyClass} mb-5`}>
            {fromSettings ? (
              <>Pick what best describes you — this fills all labels with a matching preset.</>
            ) : (
              <>
                Let&rsquo;s set up your organization.
                <br />
                First: what best describes you?
              </>
            )}
          </p>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            {WIZARD_INDUSTRIES.map((item) => {
              const active = industry === item.id;
              return (
                <button
                  key={item.id}
                  type="button"
                  onClick={() => setIndustry(item.id)}
                  className={`cursor-pointer rounded-xl p-3.5 text-left transition-all hover:shadow-lg ${
                    active
                      ? darkMode
                        ? 'ring-2 ring-indigo-500'
                        : 'ring-2 ring-indigo-600'
                      : darkMode
                        ? 'ring-1 ring-white/10'
                        : 'ring-1 ring-gray-200'
                  } ${darkMode ? 'bg-gray-800/50' : 'bg-white'}`}
                >
                  <div
                    className={`text-sm font-semibold ${darkMode ? 'text-gray-100' : 'text-gray-900'}`}
                  >
                    {item.name}
                  </div>
                  <div
                    className={`mt-0.5 text-[11px] leading-snug ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}
                  >
                    {item.description}
                  </div>
                </button>
              );
            })}
          </div>
          <p className={`mt-4 text-[11px] ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>
            This tailors wording across the platform.
          </p>
          {saveError && <p className={errorClass}>{saveError}</p>}
          <div className="mt-6 flex items-center justify-between">
            <button type="button" onClick={onClose} className={linkBtnClass}>
              {fromSettings ? 'Cancel' : 'Skip for now'}
            </button>
            <PrimaryButton size="xl" disabled={!industry} loading={saving} onClick={saveIndustry}>
              Continue
            </PrimaryButton>
          </div>
        </div>
      )}

      {step === 'region' && (
        <div>
          <h2 className={headingClass}>Currency &amp; formats</h2>
          <p className={`${bodyClass} mb-4`}>
            {signupCountry ? (
              <>
                Your organization is registered in{' '}
                <span className={`font-medium ${darkMode ? 'text-gray-200' : 'text-gray-700'}`}>
                  {getCountryName(signupCountry, 'en')}
                </span>
                , so we&rsquo;ve matched everything to that — adjust anything that doesn&rsquo;t
                fit.
              </>
            ) : (
              <>Choose your country and we&rsquo;ll match the formats to it.</>
            )}
          </p>
          <div className="mb-4">
            <Select
              label="Country"
              value={countryCode}
              onChange={(value) => applyCountry(value as string)}
              options={getCountryOptions('en')}
            />
          </div>
          <div
            className={`rounded-xl p-4 ${
              darkMode ? 'bg-gray-800/50 ring-1 ring-white/10' : 'bg-white ring-1 ring-gray-200'
            }`}
          >
            <p
              className={`mb-3 text-xs font-medium ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}
            >
              We&rsquo;ve set these for {countryName} — adjust if needed
            </p>
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <Select
                label="Base Currency"
                value={region.currency}
                onChange={(value) =>
                  setRegion((r) => ({ ...r, currency: value as CountryDefaults['currency'] }))
                }
                options={Object.entries(CURRENCIES).map(([code, currency]) => ({
                  value: code,
                  label: `${currency.symbol} ${currency.name}`,
                }))}
              />
              <Select
                label="Number format"
                value={region.numberFormat}
                onChange={(value) =>
                  setRegion((r) => ({
                    ...r,
                    numberFormat: value as CountryDefaults['numberFormat'],
                  }))
                }
                options={NUMBER_FORMAT_OPTIONS}
              />
              <Select
                label="Date format"
                value={region.dateFormat}
                onChange={(value) =>
                  setRegion((r) => ({ ...r, dateFormat: value as CountryDefaults['dateFormat'] }))
                }
                options={DATE_FORMAT_OPTIONS}
              />
              <Select
                label="Timezone"
                value={region.timezone}
                onChange={(value) => setRegion((r) => ({ ...r, timezone: value as string }))}
                options={getTimezoneOptions()}
              />
            </div>
          </div>
          {saveError && <p className={errorClass}>{saveError}</p>}
          <div className="mt-6 flex items-center justify-between">
            {stepIndex > 0 ? (
              <button
                type="button"
                onClick={() => goTo(steps[stepIndex - 1])}
                className={linkBtnClass}
              >
                ← Back
              </button>
            ) : (
              <button type="button" onClick={onClose} className={linkBtnClass}>
                Skip for now
              </button>
            )}
            <PrimaryButton size="xl" loading={saving} onClick={saveRegion}>
              Continue
            </PrimaryButton>
          </div>
        </div>
      )}

      {step === 'summary' && (
        <div>
          <h2 className={headingClass}>All set</h2>
          <p className={`${bodyClass} mb-4`}>
            {fromSettings ? (
              <>Here&rsquo;s what we configured — the label fields update right away.</>
            ) : (
              <>Here&rsquo;s what we configured. You can change everything later in Settings.</>
            )}
          </p>
          <div className="space-y-2">
            {askIndustry && (
              <div
                className={`flex items-center justify-between rounded-xl border p-3 ${
                  darkMode ? 'border-gray-700' : 'border-gray-200'
                }`}
              >
                <div>
                  <p className={`text-xs ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                    Industry
                  </p>
                  <p
                    className={`text-sm font-medium ${darkMode ? 'text-gray-100' : 'text-gray-900'}`}
                  >
                    {WIZARD_INDUSTRIES.find((item) => item.id === industry)?.name ?? '—'}
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => goTo('industry')}
                  className={`text-xs hover:underline ${darkMode ? 'text-indigo-400' : 'text-indigo-600'}`}
                >
                  Edit
                </button>
              </div>
            )}
            {askRegion && (
              <div
                className={`flex items-center justify-between rounded-xl border p-3 ${
                  darkMode ? 'border-gray-700' : 'border-gray-200'
                }`}
              >
                <div>
                  <p className={`text-xs ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                    Region
                  </p>
                  <p
                    className={`text-sm font-medium ${darkMode ? 'text-gray-100' : 'text-gray-900'}`}
                  >
                    {countryName} · {region.currency} ·{' '}
                    {NUMBER_FORMAT_OPTIONS.find((o) => o.value === region.numberFormat)?.label} ·{' '}
                    {region.dateFormat}
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => goTo('region')}
                  className={`text-xs hover:underline ${darkMode ? 'text-indigo-400' : 'text-indigo-600'}`}
                >
                  Edit
                </button>
              </div>
            )}
          </div>
          <div className="mt-6">
            <PrimaryButton size="xl" className="w-full" onClick={onClose}>
              Finish setup
            </PrimaryButton>
          </div>
        </div>
      )}
    </WizardShell>
  );
}
